/**
 * AnnotationCanvas 컴포넌트
 * 이미지 위에 형광펜/밑줄 annotation을 그리는 Canvas overlay
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Annotation, AnnotationTool, AnnotationPoint, UnderlineStyle } from '../../types/Annotation';
import {
  renderAnnotations,
  toRelativeCoord,
  samplePoints,
  snapToAxis,
  isPointOnAnnotation,
} from '../../utils/annotationUtils';

export interface AnnotationCanvasProps {
  /** Canvas 너비 (픽셀) */
  width: number;
  /** Canvas 높이 (픽셀) */
  height: number;
  /** 기존 annotation 배열 */
  annotations: Annotation[];
  /** 편집 모드 활성화 여부 */
  isEditing: boolean;
  /** 현재 선택된 도구 */
  currentTool: AnnotationTool;
  /** 현재 색상 */
  currentColor: string;
  /** 현재 선 두께 */
  currentStrokeWidth: number;
  /** 현재 투명도 (형광펜용) */
  currentOpacity: number;
  /** 현재 밑줄 스타일 */
  currentUnderlineStyle: UnderlineStyle;
  /** 형광펜 추가 콜백 */
  onHighlighterAdd: (points: AnnotationPoint[]) => void;
  /** 밑줄 추가 콜백 */
  onUnderlineAdd: (startPoint: AnnotationPoint, endPoint: AnnotationPoint) => void;
  /** annotation 삭제 콜백 */
  onAnnotationDelete: (id: string) => void;
  /** 스테이지 스케일 */
  stageScale?: number;
}

const AnnotationCanvas: React.FC<AnnotationCanvasProps> = ({
  width,
  height,
  annotations,
  isEditing,
  currentTool,
  currentColor,
  currentStrokeWidth,
  currentOpacity,
  currentUnderlineStyle,
  onHighlighterAdd,
  onUnderlineAdd,
  onAnnotationDelete,
  stageScale = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // 드로잉 상태
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState<AnnotationPoint[]>([]);
  const [startPoint, setStartPoint] = useState<AnnotationPoint | null>(null);

  // 애니메이션 프레임 ID
  const animationFrameRef = useRef<number | null>(null);

  // 기존 annotation 렌더링
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    renderAnnotations(ctx, annotations, width, height);
  }, [annotations, width, height]);

  // 프리뷰 캔버스 초기화 및 드로잉 중 렌더링
  useEffect(() => {
    const previewCanvas = previewCanvasRef.current;
    if (!previewCanvas) return;

    const ctx = previewCanvas.getContext('2d');
    if (!ctx) return;

    // 프리뷰 캔버스 초기화
    ctx.clearRect(0, 0, width, height);

    if (!isDrawing) return;

    // 현재 드로잉 중인 annotation 프리뷰 렌더링
    if (currentTool === 'highlighter' && currentPoints.length >= 2) {
      ctx.save();
      ctx.globalAlpha = currentOpacity;
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentStrokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      const firstPoint = currentPoints[0];
      ctx.moveTo(firstPoint.x * width, firstPoint.y * height);

      for (let i = 1; i < currentPoints.length; i++) {
        const point = currentPoints[i];
        ctx.lineTo(point.x * width, point.y * height);
      }

      ctx.stroke();
      ctx.restore();
    } else if (currentTool === 'underline' && startPoint && currentPoints.length > 0) {
      const endPoint = currentPoints[currentPoints.length - 1];

      ctx.save();
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentStrokeWidth;
      ctx.lineCap = 'round';

      // 스타일에 따른 선 설정
      switch (currentUnderlineStyle) {
        case 'dashed':
          ctx.setLineDash([10, 5]);
          break;
        case 'wavy':
          // 물결선은 간단히 점선으로 표시 (프리뷰용)
          ctx.setLineDash([5, 3]);
          break;
        default:
          ctx.setLineDash([]);
      }

      ctx.beginPath();
      ctx.moveTo(startPoint.x * width, startPoint.y * height);
      ctx.lineTo(endPoint.x * width, endPoint.y * height);
      ctx.stroke();

      ctx.restore();
    }
  }, [isDrawing, currentPoints, startPoint, currentTool, currentColor, currentStrokeWidth, currentOpacity, currentUnderlineStyle, width, height]);

  // 마우스 좌표를 Canvas 로컬 좌표로 변환
  const getCanvasCoords = useCallback(
    (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;

      const rect = canvas.getBoundingClientRect();
      let clientX: number, clientY: number;

      if ('touches' in e) {
        if (e.touches.length === 0) return null;
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    },
    []
  );

  // 드로잉 시작
  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isEditing || currentTool === 'none') return;

      e.preventDefault();
      e.stopPropagation();

      const coords = getCanvasCoords(e);
      if (!coords) return;

      const relativePoint = toRelativeCoord(coords.x, coords.y, width, height);

      if (currentTool === 'eraser') {
        // 지우개: 터치한 annotation 삭제
        for (const annotation of annotations) {
          if (isPointOnAnnotation(coords, annotation, width, height, 15)) {
            onAnnotationDelete(annotation.id);
            break;
          }
        }
      } else if (currentTool === 'highlighter') {
        setIsDrawing(true);
        setCurrentPoints([relativePoint]);
      } else if (currentTool === 'underline') {
        setIsDrawing(true);
        setStartPoint(relativePoint);
        setCurrentPoints([relativePoint]);
      }
    },
    [isEditing, currentTool, width, height, annotations, onAnnotationDelete, getCanvasCoords]
  );

  // 드로잉 중
  const handlePointerMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return;

      e.preventDefault();
      e.stopPropagation();

      const coords = getCanvasCoords(e);
      if (!coords) return;

      let relativePoint = toRelativeCoord(coords.x, coords.y, width, height);

      if (currentTool === 'highlighter') {
        // 형광펜: 포인트 추가 (샘플링 적용)
        setCurrentPoints((prev) => {
          const newPoints = [...prev, relativePoint];
          return samplePoints(newPoints, 0.003); // 최소 거리 0.3%
        });
      } else if (currentTool === 'underline' && startPoint) {
        // Shift 키 감지
        const isShiftKey = 'shiftKey' in e && e.shiftKey;
        if (isShiftKey) {
          relativePoint = snapToAxis(startPoint, relativePoint);
        }
        setCurrentPoints([relativePoint]);
      }
    },
    [isDrawing, currentTool, startPoint, width, height, getCanvasCoords]
  );

  // 드로잉 종료
  const handlePointerUp = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing) return;

      e.preventDefault();
      e.stopPropagation();

      if (currentTool === 'highlighter' && currentPoints.length >= 2) {
        onHighlighterAdd(currentPoints);
      } else if (currentTool === 'underline' && startPoint && currentPoints.length > 0) {
        let endPoint = currentPoints[currentPoints.length - 1];

        // 마지막 Shift 키 확인
        const isShiftKey = 'shiftKey' in e && e.shiftKey;
        if (isShiftKey) {
          endPoint = snapToAxis(startPoint, endPoint);
        }

        onUnderlineAdd(startPoint, endPoint);
      }

      setIsDrawing(false);
      setCurrentPoints([]);
      setStartPoint(null);
    },
    [isDrawing, currentTool, currentPoints, startPoint, onHighlighterAdd, onUnderlineAdd]
  );

  // 드로잉 취소 (마우스가 캔버스를 벗어났을 때)
  const handlePointerLeave = useCallback(() => {
    if (isDrawing) {
      // 진행 중인 드로잉이 있으면 완료 처리
      if (currentTool === 'highlighter' && currentPoints.length >= 2) {
        onHighlighterAdd(currentPoints);
      } else if (currentTool === 'underline' && startPoint && currentPoints.length > 0) {
        onUnderlineAdd(startPoint, currentPoints[currentPoints.length - 1]);
      }

      setIsDrawing(false);
      setCurrentPoints([]);
      setStartPoint(null);
    }
  }, [isDrawing, currentTool, currentPoints, startPoint, onHighlighterAdd, onUnderlineAdd]);

  // 정리
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // 커서 스타일 결정
  const getCursor = () => {
    if (!isEditing) return 'default';
    switch (currentTool) {
      case 'highlighter':
        return 'crosshair';
      case 'underline':
        return 'crosshair';
      case 'eraser':
        return 'pointer';
      default:
        return 'default';
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: isEditing ? 'auto' : 'none',
      }}
    >
      {/* 기존 annotation 렌더링 캔버스 */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      />

      {/* 드로잉 프리뷰 캔버스 */}
      <canvas
        ref={previewCanvasRef}
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor: getCursor(),
        }}
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerLeave}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
      />
    </div>
  );
};

export default AnnotationCanvas;
